'use client';

import { useContext } from 'react';
import cn from 'classnames';
import map from 'lodash/map';

import createStyles from '@guestyci/foundation/createStyles';
import TextField from '@guestyci/foundation/TextField';
import { Row } from '@guestyci/foundation/Layout';
import Icon from '@guestyci/foundation/Icon';
import t from '@guestyci/localize/t.macro/t.macro';
import { Button } from '@guestyci/arc';

import { Link, usePathname } from 'i18n/routing';
import socialIcons from 'constants/socialIcons';
import { WebsiteSettingsContext } from 'context/WebsiteSettingsContext';

const useStyles = createStyles(({ breakpoints: { create } }) => ({
  root: {
    display: 'flex',
    justifyContent: 'center',
    minHeight: 216,
    height: '100%',
    background: ({ footerBg }) => footerBg || '#212121',
    padding: '20px 0',
    '& a:focus-within': {
      '--tw-ring-color': ({ footerTxt }) => `${footerTxt} !important`,
    },
  },
  content: {
    width: '100%',
    maxWidth: 625,
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    color: ({ footerTxt }) => footerTxt || '#fff',
  },
  title: {
    color: ({ footerTxt }) => footerTxt || '#fff',
  },
  links: {
    display: 'flex',
    listStyleType: 'none',
    margin: 0,
    padding: 0,
    width: '100%',
    justifyContent: 'space-around',
    marginTop: 10,
    '& > a': {
      color: ({ footerTxt }) => footerTxt || '#fff',
      cursor: 'pointer',
    },
  },
  socialMedia: {
    width: 140,
    justifyContent: 'space-evenly',
    marginTop: 10,
  },
  phoneNumbers: {
    display: 'flex',
    flexDirection: 'column',
    '& a': {
      color: ({ footerTxt }) => footerTxt || '#fff',
    },
  },
  socialIcon: {
    fill: ({ footerBg }) => footerBg || '#212121',
    '& path': {
      stroke: ({ footerTxt }) => footerTxt || '#fff',
    },
    '& circle': {
      stroke: ({ footerTxt }) => footerTxt || '#fff',
    },
    '& rect': {
      stroke: ({ footerTxt }) => footerTxt || '#fff',
    },
    '& g': {
      '& path': {
        fill: ({ footerTxt }) => footerTxt || '#212121',
      },
    },
  },
}));

const renderPhones = (phones) =>
  phones.map((phone) => (
    <TextField key={phone.number}>
      <a href={`TEL:${phone.number}`}>{phone.number}</a>
    </TextField>
  ));

const renderSocial = (socials, className) => {
  return map(socials, (url, type) => {
    const socialUrl = typeof url === 'object' ? url?.url : url;
    const socialType = typeof url === 'object' ? url.type : type;
    const platformName = socialType.charAt(0).toUpperCase() + socialType.slice(1).toLowerCase();
    return (
      <Button variant="plain-primary" className="gst-p-0 gst-font-normal" asChild>
        <a
          key={socialUrl} 
          rel="noreferrer" 
          target="_blank" 
          href={`${socialUrl.includes('://') ? '' : '//'}${socialUrl}`}
          aria-label={`Visit our ${platformName} page`}
          className="gst-font-normal"
        >
          <Icon svg={socialIcons[socialType.toUpperCase()]} size={23} className={className} />
        </a>
      </Button>
    );
  });
};

const Footer = () => {
  const pathname = usePathname();
  const {
    companyInfo: { address, email, name, phones = [] } = {},
    contentConfiguration: { colorTheme: { colors = {} } = {}, social = null } = {},
    isLoading,
  } = useContext(WebsiteSettingsContext);
  const { root, title, content, links, socialMedia, phoneNumbers, socialIcon } = useStyles({
    footerBg: colors?.footerBackground,
    footerTxt: colors?.footerText,
  });

  if (isLoading) {
    return null;
  }

  return (
    <div data-footer className={root}>
      <div className={content}>
        <TextField align="center" variant="h2" className={title}>
          {name}
        </TextField>
        <TextField variant="h5" className="mt-2 pr-4 pl-4">
          {address}
        </TextField>
        <TextField variant="h5" className="mt-2">
          {email}
        </TextField>
        {phones.length > 0 && <div className={cn('mt-2', phoneNumbers)}>{renderPhones(phones)}</div>}

        <div className={links}>
          <Button variant="plain-primary" className="gst-p-0 gst-font-normal" asChild>
            <Link
              target="_blank"
              href="/privacy-policy"
              aria-label={t('Privacy policy opens in a new tab')}
              className="gst-underline"
              style={{ color: colors?.footerText || 'inherit' }}
              aria-current={pathname.includes('/privacy-policy') ? 'page' : false}
            >
              {t('Privacy Policy')}
            </Link>
          </Button>
        </div>
        {social && <Row className={socialMedia}>{renderSocial(social, socialIcon)}</Row>}
      </div>
    </div>
  );
};

export default Footer;
